// #ux-redesign-start: ThemeProvider + useTheme hook (Paso 1)
// Contexto del tema activo (light/dark). Setea data-theme en <html> y persiste
// la elección del usuario en localStorage 'tsd-theme'.
//
// Decision (2026-05-17): el toggle es binario — Light o Dark. La opción "System"
// se eliminó por feedback del usuario (genera ambigüedad sobre qué está activo).
// La primera vez (sin valor en localStorage) se resuelve `prefers-color-scheme`
// como default, pero a partir de ahí solo persisten 'light' o 'dark'.
//
// PORTABILIDAD: este archivo es trasplantable a Web-Patients sin cambios.
// Solo asegurarse de que <ThemeProvider> envuelva la app en index.js.
//
// USO:
//   import { ThemeProvider, useTheme } from 'contexts/ThemeContext';
//
//   <ThemeProvider><App /></ThemeProvider>
//
//   const { theme, setTheme, resolvedTheme } = useTheme();
//   // theme === resolvedTheme: 'light' | 'dark'
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'tsd-theme';
const VALID_THEMES = ['light', 'dark'];

const ThemeContext = createContext({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
});

const resolveSystemTheme = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const readStoredTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (VALID_THEMES.indexOf(stored) !== -1) return stored;
  } catch (e) {
    // localStorage puede no estar disponible (private mode, etc.)
  }
  return null;
};

const applyThemeAttr = (resolved) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
  document.documentElement.setAttribute('data-theme-preference', resolved);
};

export const ThemeProvider = ({ children, defaultTheme }) => {
  const [theme, setThemeState] = useState(() => {
    const stored = readStoredTheme();
    if (stored) return stored;
    if (VALID_THEMES.indexOf(defaultTheme) !== -1) return defaultTheme;
    return resolveSystemTheme();
  });

  useEffect(() => {
    applyThemeAttr(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (VALID_THEMES.indexOf(next) === -1) return;
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // ignore
    }
  }, []);

  const value = {
    theme,
    resolvedTheme: theme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
// #ux-redesign-end
